import { useAuth } from "@/context/auth";
import useDashboardAccess from "@/hooks/useDashboardAccess";
import useIsSmall from "@/hooks/useIsSmall";
import { MerchantUserType } from "@/models/club";
import classes from "@/modules/mobileHome/home.module.scss";
import { SectionTile } from "@/modules/mobileHome/SectionTile";
import { HomeRole, sectionGroups, sectionsFor } from "@/modules/mobileHome/sections";
import { useRoleBadges } from "@/modules/mobileHome/useSectionBadges";
import useGetMerchant from "@/pages/dashboard/profile/hooks/useGetMerchant";
import { useGetMerchantForMerchantUser } from "@/services/merchant/utilities";
import { useReviewAccess } from "@/services/reviews/useReviewAccess";
import { useGetImage } from "@/utilities/helper/getImage";
import { merchantSiteUrl } from "@/utilities/helper/merchantSite";
import { Avatar } from "@mantine/core";
import { IconBell, IconChevronLeft, IconFileCertificate, IconGift, IconLogout, IconWorld } from "@tabler/icons-react";
import { useMemo } from "react";
import { Link, Navigate } from "react-router-dom";

// Every section a role can open, grouped the same way as the home shortcuts.
export default function MenuPage() {
  const isSmall = useIsSmall();
  const { isManager } = useDashboardAccess();
  const { canRead: canReadReviews } = useReviewAccess();
  const { logout, getUserByToken } = useAuth();
  const phone = getUserByToken()?.phoneNumber ?? "";
  const member = useGetMerchantForMerchantUser(phone);
  const { data: merchant } = useGetMerchant();
  const { imageUrl } = useGetImage({ bucket: "merchant", image: merchant?.logo });

  const role: HomeRole = isManager ? "manager" : "stylist";
  const isRenter = Boolean((member.data as { isChairRenter?: boolean } | undefined)?.isChairRenter);
  const sections = useMemo(() => sectionsFor(role, canReadReviews, isRenter), [role, canReadReviews, isRenter]);
  const badges = useRoleBadges();

  // The sidebar already lists every section on larger screens.
  if (!isSmall) return <Navigate to="/dashboard" replace />;

  const salonTitle = merchant?.businessInfo?.title || "";
  const fullName = `${member.data?.firstName ?? ""} ${member.data?.lastName ?? ""}`.trim();
  const isOwner = Boolean(phone && merchant?.phoneNumber === phone);
  const roleLabel = isOwner ? "مالک مجموعه" : isManager ? "مدیر مجموعه" : member.data?.jobTitle?.trim() || "آرایشگر";
  const siteUrl = merchantSiteUrl(merchant?.businessInfo?.subDomain);
  // The profile page only lets the salon admin manage notifications and invitations.
  const isAdmin = member.data?.merchantUserType === MerchantUserType.Value0;

  const profile = (
    <>
      <Avatar src={isManager ? imageUrl : null} size={48} radius="xl" color="myColor" aria-hidden>
        {(isManager ? salonTitle : fullName).charAt(0)}
      </Avatar>
      <span className={classes.profileText}>
        <strong>{isManager ? salonTitle : fullName || "حساب من"}</strong>
        <span>{isManager ? [fullName, roleLabel].filter(Boolean).join(" · ") : [roleLabel, salonTitle].filter(Boolean).join(" · ")}</span>
      </span>
    </>
  );

  return (
    <div className={classes.page}>
      <header className={classes.header}>
        <h1 className={classes.menuTitle}>منو</h1>
      </header>

      <div className={classes.menuBody}>
        <section className={classes.card}>
          {isManager ? (
            <Link to="/dashboard/profile" className={classes.profile}>
              {profile}
              <IconChevronLeft className={classes.chevron} size={20} stroke={1.7} aria-hidden />
            </Link>
          ) : (
            <div className={classes.profile}>{profile}</div>
          )}
          {siteUrl && (
            <a href={siteUrl} target="_blank" rel="noopener noreferrer" className={classes.siteLink}>
              <IconWorld size={18} stroke={1.7} aria-hidden />
              مشاهده‌ی سایت مجموعه
            </a>
          )}
        </section>

        {sectionGroups.map((group) => {
          const items = sections.filter((section) => section.group === group.id);
          if (!items.length) return null;
          return (
            <section key={group.id} className={classes.group} aria-labelledby={`menu-${group.id}`}>
              <h2 id={`menu-${group.id}`} className={classes.groupTitle}>
                {group.label[role]}
              </h2>
              <div className={`${classes.card} ${classes.grid}`}>
                {items.map((section) => (
                  <SectionTile key={section.id} section={section} count={section.badge ? badges[section.badge] : undefined} />
                ))}
              </div>
            </section>
          );
        })}

        <section className={classes.card} aria-label="حساب کاربری">
          {isAdmin && (
            <>
              <Link to="/dashboard/profile/notifications" className={classes.row}>
                <IconBell size={20} stroke={1.7} aria-hidden />
                <span>اعلان‌ها</span>
                <IconChevronLeft size={18} stroke={1.7} aria-hidden />
              </Link>
              <Link to="/dashboard/profile/referralCode" className={classes.row}>
                <IconGift size={20} stroke={1.7} aria-hidden />
                <span>دعوت از دوستان</span>
                <IconChevronLeft size={18} stroke={1.7} aria-hidden />
              </Link>
            </>
          )}
          {isManager && (
            <Link to="/dashboard/profile/termsAndCondition" className={classes.row}>
              <IconFileCertificate size={20} stroke={1.7} aria-hidden />
              <span>قوانین و مقررات</span>
              <IconChevronLeft size={18} stroke={1.7} aria-hidden />
            </Link>
          )}
          <button type="button" className={classes.row} data-danger onClick={logout}>
            <IconLogout size={20} stroke={1.7} aria-hidden />
            <span>خروج از حساب</span>
          </button>
        </section>
      </div>
    </div>
  );
}
